import { Injectable, NgZone } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Analytics, getAnalytics } from 'firebase/analytics';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { ActionPerformed as localNotificationActionPerformed, LocalNotifications } from '@capacitor/local-notifications';
import { from } from 'rxjs';
import { ActionPerformed, PushNotifications, PushNotificationSchema } from '@capacitor/push-notifications';
import { AppFeaturesService } from '../portal/app-features.service';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { ReviewFormPreviewModal } from '../shared-components/review-form/review-form.state';
import { NotificationType } from '../shared';
import { Store } from '@ngxs/store';
import { AuthService } from '../portal/auth.service';
import { UserGetNotifications, UserUpdateNotification } from '../portal/userDetails.state';
import { AppFeaturesState } from '../portal/app-feature.state';
import { SetLoading } from './loading.state';
import { VehicleViewUpdateVehicleDetailsOptions } from '../inside/schedule-services/vehicle-v2/vehicles-view.state';
import { InsideNavigateToHome, InsideNavigateToNotifications } from '../portal/inside.state';
import { logger } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  app: FirebaseApp;
  // analytics: Analytics;
  logger = logger.for('FirebaseService');

  localNotificationCounter = 0;
  constructor(
    private appFeaturesService: AppFeaturesService,
    private store: Store,
    private authService: AuthService,
    private zone: NgZone
  ) {
    this.logger.log('PushNotifications: initialising push notification listeners');
    if (Capacitor.isPluginAvailable('PushNotifications')) {
      this.initializePushNotifications();
      this.logger.log('PushNotifications: initialised push notification listeners');
    }

    if (Capacitor.isPluginAvailable('LocalNotifications')) {
      this.initializeLocalNotification();
    }
  }

  initializeFirebase() {
    this.app = initializeApp({
      ...environment.firebase.firebaseConfig,
      measurementId: this.appFeaturesService.googleAnalyticsMeasurementId,
    });
    // setTimeout(() => {
    //   this.analytics = getAnalytics(this.app);
    // }, 100);
  }

  requestPermission() {
    const messaging = getMessaging();
    console.log('messaging', messaging);
    const observable = from(getToken(messaging, { vapidKey: environment.firebase.vapidKey }));
    return observable;
  }

  listen() {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      console.log('Message received. ', payload);
    });
  }

  async initializePushNotifications() {
    this.logger.log('PushNotifications: setting up PushNotifications');
    await PushNotifications.addListener('pushNotificationActionPerformed', (notificationActionPerformed: ActionPerformed) => {
      this.onPushNotificationActionPerformed(notificationActionPerformed);
    });

    await PushNotifications.addListener('registrationError', (error: any) => {
      this.logger.warn('Error on registration: ' + JSON.stringify(error));
    });

    await PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      this.logger.log('Push received: ' + JSON.stringify(notification));
      // To handle foreground notifications properly we recreate them as local notifications
      this.scheduleLocalNotification(notification);
      this.store.dispatch(new UserGetNotifications());
    });

    // Remove the early global startup listener now that the runtime listener is registered
    const globalListener: PluginListenerHandle = await (window as any).globalPushListener;
    if (globalListener && typeof globalListener.remove === 'function') {
      this.logger.log('PushNotifications: removing global startup listener');
      await globalListener.remove();
      delete (window as any).globalPushListener;
    }

    // Process any buffered actions that occurred during startup
    setTimeout(() => {
      const pending = (window as any).pendingNotificationActions;
      this.logger.log('PushNotifications: checking pending notifications:', pending.length);
      if (pending && pending.length > 0) {
        this.logger.log('PushNotifications: processing pending buffered actions:', pending.length);
        while (pending.length > 0) {
          const action = pending.shift();
          this.zone.run(() => {
            this.onPushNotificationActionPerformed(action);
          });
        }
      }
    }, 2000);
  }

  private onPushNotificationActionPerformed(notificationActionPerformed: ActionPerformed) {
    this.logger.log('PushNotifications: handling action');
    const data = notificationActionPerformed.notification.data.data
      ? JSON.parse(notificationActionPerformed.notification.data.data)
      : undefined;

    this.logger.log('PushNotifications: notification data', JSON.stringify(data));
    const url = data?.navigate_to;
    if (url) {
      this.logger.log('PushNotifications: url', url);
      this.store.dispatch(new SetLoading(true, this, true, this.initializePushNotifications.name));
      setTimeout(() => {
        this.store.dispatch(new SetLoading(false, this, true, this.initializePushNotifications.name));
        this.appFeaturesService.inAppNavigationHandler(url);
        this.handleNotificationAction(url);
      }, 3000);
    }
  }

  handleNotificationAction(url: string) {
    this.logger.log('notification: handling notification');
    const featureFlags = this.store.selectSnapshot(AppFeaturesState.featureFlags);
    if (!featureFlags.notifications) {
      return;
    }
    if (!this.authService.getToken()) {
      return;
    }
    const notificationInfo = this.appFeaturesService.getNotificationInfoFromQuery(url);
    if (!notificationInfo) {
      return;
    }
    if (notificationInfo.usersNotificationsId) {
      this.store.dispatch(new UserUpdateNotification(notificationInfo.usersNotificationsId));
    }
    this.logger.log('notification: check for order review notification');
    if (notificationInfo.notificationType === NotificationType.ORDER_REVIEW) {
      this.logger.log('notification: handling order review');
      this.store.dispatch(new InsideNavigateToNotifications());
      this.logger.log('notification: triggering navigation to notification');
      this.store.dispatch(new ReviewFormPreviewModal(notificationInfo.ordersId, parseInt(notificationInfo.ordersServicesId)));
      this.logger.log('notification: done');
    }
    if (notificationInfo.notificationType === NotificationType.REQUEST_VIN_NUMBER) {
      this.store.dispatch(
        new VehicleViewUpdateVehicleDetailsOptions({
          backAction: new InsideNavigateToHome(),
          loadVehicles: true,
        })
      );
    }
  }

  initializeLocalNotification() {
    LocalNotifications.addListener('localNotificationReceived', () => {
      this.store.dispatch(new UserGetNotifications());
    });
    LocalNotifications.addListener('localNotificationActionPerformed', (event: localNotificationActionPerformed) => {
      this.logger.log('localNotificationActionPerformed', JSON.stringify(event));
      const url = event.notification.extra.navigateTo;
      if (url) {
        this.logger.log('navigating', url);
        this.appFeaturesService.inAppNavigationHandler(url);
        this.handleNotificationAction(url);
      }
    });
  }

  public scheduleLocalNotification(notification: PushNotificationSchema) {
    this.localNotificationCounter += 1;
    const data = notification.data.data ? JSON.parse(notification.data.data) : undefined;
    const localNotificationData = {
      notifications: [
        {
          title: notification.title ?? 'AllRotors',
          body: notification.body ?? '',
          id: this.localNotificationCounter,
          smallIcon: 'res/drawable/ic_notification',
          extra: {
            navigateTo: data?.navigate_to,
          },
        },
      ],
    };
    LocalNotifications.schedule(localNotificationData);
  }

  async setCurrentScreen() {
    // setCurrentScreen({
    //   screenName: 'Login',
    //   screenClassOverride: 'LoginPage',
    // });
  }
}
